import { apiClient } from './api-client';

// ==================== Enums ====================

export enum IntegrationType {
  SALESFORCE = 'salesforce',
  HUBSPOT = 'hubspot',
  ZENDESK = 'zendesk',
  FRESHDESK = 'freshdesk',
  INTERCOM = 'intercom',
  SLACK = 'slack',
  TEAMS = 'teams',
  JIRA = 'jira',
  GITHUB = 'github',
  CUSTOM = 'custom',
}

/**
 * The values the hub actually sends, plus the ones this client used to assume.
 *
 * The connector rows are stored against a Prisma enum
 * (ACTIVE / INACTIVE / ERROR / PENDING_AUTH) which serialises uppercase, while
 * this enum declared a lowercase vocabulary of its own. Nothing compared the
 * two until 20f, so the mismatch had never surfaced — any code that had
 * compared a live status against `IntegrationStatus.CONNECTED` would simply
 * never have matched. Both vocabularies are declared here, and
 * `features/integrations/connectors.ts` is the one place that maps either onto
 * a rendered state.
 */
export enum IntegrationStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ERROR = 'ERROR',
  PENDING_AUTH = 'PENDING_AUTH',
  /** @deprecated Older lowercase rows. Still read, never written. */
  CONNECTED = 'connected',
  /** @deprecated */
  DISCONNECTED = 'disconnected',
  /** @deprecated */
  LEGACY_ERROR = 'error',
  /** @deprecated */
  PENDING = 'pending',
}

/**
 * An event name, e.g. `ticket.created`. The list of events a webhook can
 * receive comes from integration-hub (`getWebhookEventTypes`), not from here:
 * a list kept in this client drifted (it offered chat events that are never
 * sent and missed `ticket.resolved` and friends).
 */
export type WebhookEvent = string;

/** One entry of integration-hub's event catalog. */
export interface WebhookEventType {
  type: WebhookEvent;
  group: string;
  label: string;
  description: string;
}

/**
 * Used only when the catalog request fails, so the form stays usable. Mirrors
 * integration-hub's `webhook-event-catalog.ts`; the hub's list wins whenever
 * it loads.
 */
export const FALLBACK_WEBHOOK_EVENT_TYPES: WebhookEventType[] = [
  { type: 'ticket.created', group: 'ticket', label: 'Ticket created', description: 'A ticket was created, from any channel or the API.' },
  { type: 'ticket.updated', group: 'ticket', label: 'Ticket updated', description: 'One or more ticket fields changed.' },
  { type: 'ticket.assigned', group: 'ticket', label: 'Ticket assigned', description: 'The ticket was assigned or reassigned to an agent.' },
  { type: 'ticket.status_changed', group: 'ticket', label: 'Ticket status changed', description: 'Any status change, with the previous and new status.' },
  { type: 'ticket.resolved', group: 'ticket', label: 'Ticket resolved', description: 'The ticket moved to Resolved.' },
  { type: 'ticket.closed', group: 'ticket', label: 'Ticket closed', description: 'The ticket moved to Closed.' },
  { type: 'ticket.reopened', group: 'ticket', label: 'Ticket reopened', description: 'A resolved or closed ticket was reopened.' },
  { type: 'ticket.message_added', group: 'ticket', label: 'Ticket message added', description: 'A customer-visible message was added (customer, agent or automatic reply), with its full body. Internal notes are never sent here.' },
  { type: 'ticket.note_added', group: 'ticket', label: 'Ticket internal note added', description: 'An agent-only internal note was added, with its full body. Subscribe only if your system may hold internal notes.' },
  { type: 'customer.created', group: 'customer', label: 'Customer created', description: 'A customer record was created.' },
  { type: 'customer.updated', group: 'customer', label: 'Customer updated', description: 'A customer record was changed.' },
  { type: 'customer.deleted', group: 'customer', label: 'Customer deleted or merged', description: 'A customer was deleted, or merged into another customer (reason merged, with the customer that now holds its tickets).' },
  { type: 'kb_article.changed', group: 'knowledge', label: 'Knowledge article changed', description: 'A knowledge-base article was created, updated or deleted.' },
  { type: 'kb_category.changed', group: 'knowledge', label: 'Knowledge category changed', description: 'A knowledge-base category was created, updated or deleted.' },
];

/**
 * Split a webhook's subscribed events into the ones the catalog knows and the
 * ones it does not (e.g. `chat.started` from the old client list, which is
 * never sent). The edit form keeps the first and drops the second.
 */
export function partitionWebhookEvents(
  events: readonly WebhookEvent[],
  catalog: readonly WebhookEventType[],
): { known: WebhookEvent[]; unknown: WebhookEvent[] } {
  const types = new Set(catalog.map((entry) => entry.type));
  return {
    known: events.filter((event) => types.has(event)),
    unknown: events.filter((event) => !types.has(event)),
  };
}

export enum DeliveryStatus {
  PENDING = 'pending',
  SUCCESS = 'success',
  RETRYING = 'retrying',
  FAILED = 'failed',
}

// ==================== Interfaces ====================

export interface Integration {
  id: string;
  tenantId: string;
  name: string;
  type: IntegrationType;
  status: IntegrationStatus;
  config: Record<string, any>;
  metadata?: Record<string, any>;
  isEnabled: boolean;
  /**
   * When this connector last SUCCEEDED — not when it was last attempted.
   *
   * The hub writes it on a successful probe and passes `undefined` on a
   * failure, which Prisma reads as "leave it alone", so a failing connector
   * keeps the timestamp of the last time it worked. That is what makes "last
   * succeeded" an answerable question on the integrations grid.
   */
  lastSyncAt?: string;
  /** What the hub recorded the last time this connector failed. */
  errorLog?: { error?: string; timestamp?: string; [key: string]: unknown };
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateIntegrationDto {
  name: string;
  type: IntegrationType;
  config?: Record<string, any>;
  credentials?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface UpdateIntegrationDto {
  name?: string;
  config?: Record<string, any>;
  credentials?: Record<string, any>;
  metadata?: Record<string, any>;
  isEnabled?: boolean;
}

export interface Webhook {
  id: string;
  tenantId: string;
  name: string;
  url: string;
  events: WebhookEvent[];
  headers?: Record<string, string>;
  isEnabled: boolean;
  /** Still signed with the old publicly known placeholder secret: rotate it. */
  usesDefaultSecret?: boolean;
  /** While a rotation is in progress, when the previous secret stops signing. */
  secretRotationEndsAt?: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Create response: `secret` is present, once, when one was generated. */
export interface CreatedWebhook extends Webhook {
  secret?: string;
}

/** A new signing secret. Shown once; it cannot be read back later. */
export interface RotatedWebhookSecret {
  webhookId: string;
  secret: string;
  rotatedAt: string;
  /** When the previous secret stops signing. */
  expiresAt: string;
  windowHours: number;
}

export interface CreateWebhookDto {
  name: string;
  url: string;
  events: WebhookEvent[];
  secret?: string;
  headers?: Record<string, string>;
  isEnabled?: boolean;
}

export interface UpdateWebhookDto {
  name?: string;
  url?: string;
  events?: WebhookEvent[];
  secret?: string;
  headers?: Record<string, string>;
  isEnabled?: boolean;
}

export interface WebhookDelivery {
  id: string;
  webhookId: string;
  event: WebhookEvent;
  payload: Record<string, any>;
  status: DeliveryStatus;
  statusCode?: number;
  responseBody?: string;
  attempts: number;
  nextRetryAt?: string;
  deliveredAt?: string;
  createdAt: string;
}

export interface TestResult {
  success: boolean;
  message: string;
}

// ==================== Service ====================

class IntegrationService {
  // ==================== Integrations ====================

  async createIntegration(dto: CreateIntegrationDto): Promise<Integration> {
    const response = await apiClient.post<Integration>('/integrations', dto);
    return response.data;
  }

  async getIntegrations(): Promise<Integration[]> {
    const response = await apiClient.get<Integration[]>('/integrations');
    return response.data;
  }

  async getIntegrationById(id: string): Promise<Integration> {
    const response = await apiClient.get<Integration>(`/integrations/${id}`);
    return response.data;
  }

  async updateIntegration(id: string, dto: UpdateIntegrationDto): Promise<Integration> {
    const response = await apiClient.put<Integration>(`/integrations/${id}`, dto);
    return response.data;
  }

  async deleteIntegration(id: string): Promise<void> {
    await apiClient.delete(`/integrations/${id}`);
  }

  async testConnection(id: string): Promise<TestResult> {
    const response = await apiClient.post<TestResult>(`/integrations/${id}/test-connection`);
    return response.data;
  }

  async syncIntegration(id: string): Promise<TestResult> {
    const response = await apiClient.post<TestResult>(`/integrations/${id}/sync`);
    return response.data;
  }

  // ==================== Webhooks ====================

  async createWebhook(dto: CreateWebhookDto): Promise<CreatedWebhook> {
    const response = await apiClient.post<CreatedWebhook>('/integrations/webhooks', dto);
    return response.data;
  }

  async getWebhookEventTypes(): Promise<WebhookEventType[]> {
    const response = await apiClient.get<WebhookEventType[]>('/integrations/webhooks/event-types');
    return response.data;
  }

  /** `windowHours`: how long the previous secret keeps signing (1-168, default 24). */
  async rotateWebhookSecret(id: string, windowHours?: number): Promise<RotatedWebhookSecret> {
    const response = await apiClient.post<RotatedWebhookSecret>(
      `/integrations/webhooks/${id}/rotate-secret`,
      windowHours === undefined ? {} : { windowHours },
    );
    return response.data;
  }

  async getWebhooks(): Promise<Webhook[]> {
    const response = await apiClient.get<Webhook[]>('/integrations/webhooks');
    return response.data;
  }

  async getWebhookById(id: string): Promise<Webhook> {
    const response = await apiClient.get<Webhook>(`/integrations/webhooks/${id}`);
    return response.data;
  }

  async updateWebhook(id: string, dto: UpdateWebhookDto): Promise<Webhook> {
    const response = await apiClient.put<Webhook>(`/integrations/webhooks/${id}`, dto);
    return response.data;
  }

  async deleteWebhook(id: string): Promise<void> {
    await apiClient.delete(`/integrations/webhooks/${id}`);
  }

  async testWebhook(id: string): Promise<TestResult> {
    const response = await apiClient.post<TestResult>(`/integrations/webhooks/${id}/test`);
    return response.data;
  }

  async getWebhookDeliveries(id: string, limit = 50): Promise<WebhookDelivery[]> {
    const response = await apiClient.get<WebhookDelivery[]>(
      `/integrations/webhooks/${id}/deliveries?limit=${limit}`
    );
    return response.data;
  }

  async retryDelivery(deliveryId: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(
      `/integrations/webhooks/deliveries/${deliveryId}/retry`
    );
    return response.data;
  }

  async verifyWebhookUrl(url: string): Promise<{ reachable: boolean; statusCode?: number; message: string }> {
    const response = await apiClient.post<{ reachable: boolean; statusCode?: number; message: string }>(
      '/integrations/webhooks/verify-url',
      { url },
    );
    return response.data;
  }

  // ==================== Helpers ====================

  getIntegrationTypeLabel(type: IntegrationType): string {
    const labels: Record<IntegrationType, string> = {
      [IntegrationType.SALESFORCE]: 'Salesforce',
      [IntegrationType.HUBSPOT]: 'HubSpot',
      [IntegrationType.ZENDESK]: 'Zendesk',
      [IntegrationType.FRESHDESK]: 'Freshdesk',
      [IntegrationType.INTERCOM]: 'Intercom',
      [IntegrationType.SLACK]: 'Slack',
      [IntegrationType.TEAMS]: 'Microsoft Teams',
      [IntegrationType.JIRA]: 'Jira',
      [IntegrationType.GITHUB]: 'GitHub',
      [IntegrationType.CUSTOM]: 'Custom',
    };
    return labels[type] || type;
  }

  getIntegrationTypeIcon(type: IntegrationType): string {
    const icons: Record<IntegrationType, string> = {
      [IntegrationType.SALESFORCE]: '☁️',
      [IntegrationType.HUBSPOT]: '🧡',
      [IntegrationType.ZENDESK]: '💚',
      [IntegrationType.FRESHDESK]: '🌿',
      [IntegrationType.INTERCOM]: '💬',
      [IntegrationType.SLACK]: '💼',
      [IntegrationType.TEAMS]: '🟦',
      [IntegrationType.JIRA]: '🔷',
      [IntegrationType.GITHUB]: '🐙',
      [IntegrationType.CUSTOM]: '⚙️',
    };
    return icons[type] || '🔌';
  }

  // getStatusColor lived here and was keyed by the lowercase vocabulary above,
  // so it returned the neutral fallback for every status the hub actually
  // sends. Nothing consumed it. Rendering state now belongs to
  // `features/integrations/connectors.ts`, which normalises both vocabularies
  // and has tests holding the mapping.

  /** The catalog label for `event`, or the raw name for one it does not know. */
  getWebhookEventLabel(
    event: WebhookEvent,
    catalog: readonly WebhookEventType[] = FALLBACK_WEBHOOK_EVENT_TYPES,
  ): string {
    return catalog.find((entry) => entry.type === event)?.label || event;
  }

  getDeliveryStatusColor(status: DeliveryStatus): string {
    const colors: Record<DeliveryStatus, string> = {
      [DeliveryStatus.SUCCESS]: 'bg-green-100 text-green-700',
      [DeliveryStatus.PENDING]: 'bg-yellow-100 text-yellow-700',
      [DeliveryStatus.RETRYING]: 'bg-yellow-100 text-yellow-700',
      [DeliveryStatus.FAILED]: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  }
}

export const integrationService = new IntegrationService();
export default integrationService;
